import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Person } from '../types';
import { peopleApi } from '../services/api';

interface PeopleState {
  people: Person[];
  loading: boolean;
  error: string | null;
}

const initialState: PeopleState = {
  people: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchPeople = createAsyncThunk(
  'people/fetchPeople',
  async (_, { rejectWithValue }) => {
    try {
      return await peopleApi.getAll();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchPeopleByLocation = createAsyncThunk(
  'people/fetchPeopleByLocation',
  async (locationId: string, { rejectWithValue }) => {
    try {
      return await peopleApi.getByLocation(locationId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const createPersonAsync = createAsyncThunk(
  'people/createPerson',
  async (person: Person, { rejectWithValue }) => {
    try {
      return await peopleApi.create(person);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const updatePersonAsync = createAsyncThunk(
  'people/updatePerson',
  async (person: Person, { rejectWithValue }) => {
    try {
      return await peopleApi.update(person);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const deletePersonAsync = createAsyncThunk(
  'people/deletePerson',
  async (id: string, { rejectWithValue }) => {
    try {
      await peopleApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const peopleSlice = createSlice({
  name: 'people',
  initialState,
  reducers: {
    setPeople: (state, action: PayloadAction<Person[]>) => {
      state.people = action.payload;
    },
    addPerson: (state, action: PayloadAction<Person>) => {
      state.people.push(action.payload);
    },
    updatePerson: (state, action: PayloadAction<Person>) => {
      const index = state.people.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.people[index] = action.payload;
      }
    },
    removePerson: (state, action: PayloadAction<string>) => {
      state.people = state.people.filter(p => p.id !== action.payload);
    },
    clearPeopleError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch people
      .addCase(fetchPeople.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPeople.fulfilled, (state, action) => {
        state.loading = false;
        state.people = action.payload;
      })
      .addCase(fetchPeople.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch people by location
      .addCase(fetchPeopleByLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPeopleByLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.people = action.payload;
      })
      .addCase(fetchPeopleByLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create person
      .addCase(createPersonAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPersonAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.people.push(action.payload);
      })
      .addCase(createPersonAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update person
      .addCase(updatePersonAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePersonAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.people.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.people[index] = action.payload;
        }
      })
      .addCase(updatePersonAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete person
      .addCase(deletePersonAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePersonAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.people = state.people.filter(p => p.id !== action.payload);
      })
      .addCase(deletePersonAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPeople, addPerson, updatePerson, removePerson, clearPeopleError } = peopleSlice.actions;
export default peopleSlice.reducer;
